import type { MediaIncludeOptions } from "../types";
import { clampPerPage } from "../utils";
import {
  CHARACTER_FIELDS,
  CHARACTER_FIELDS_COMPACT,
  MEDIA_FIELDS,
  MEDIA_FIELDS_BASE,
  MEDIA_RECOMMENDATION_FIELDS,
  RELATIONS_FIELDS,
  STAFF_FIELDS,
  VOICE_ACTOR_FIELDS_COMPACT,
} from "./fragments";
import {
  QUERY_MEDIA_BY_ID,
  QUERY_MEDIA_BY_SEASON,
  QUERY_MEDIA_SEARCH,
  QUERY_PLANNING,
  QUERY_RECENT_CHAPTERS,
  QUERY_TRENDING,
} from "./media";

/**
 * Build extra part of media query which includes characters, staff,
 * relations, streaming episodes, external links, stats, and recommendations.
 *
 * When no include options are given, an empty array is returned.
 *
 * @internal
 */
function buildMediaIncludeQuery(include: MediaIncludeOptions): string[] {
  const extra: string[] = [];

  if (include.relations !== false) {
    extra.push(RELATIONS_FIELDS);
  }

  if (include.characters) {
    const opts = typeof include.characters === "object" ? include.characters : {};
    const perPage = clampPerPage(opts.perPage ?? 25);
    const sortClause = opts.sort !== false ? ", sort: [ROLE, RELEVANCE, ID]" : "";
    const voiceActorBlock = opts.voiceActors
      ? `\n            voiceActors {
              ${VOICE_ACTOR_FIELDS_COMPACT}
            }`
      : "";
    extra.push(`
    characters(perPage: ${perPage}${sortClause}) {
      edges {
        role
        node {
          ${CHARACTER_FIELDS_COMPACT}
        }${voiceActorBlock}
      }
    }`);
  }

  if (include.staff) {
    const opts = typeof include.staff === "object" ? include.staff : {};
    const perPage = clampPerPage(opts.perPage ?? 25);
    const sortClause = opts.sort !== false ? ", sort: [RELEVANCE, ID]" : "";
    extra.push(`
    staff(perPage: ${perPage}${sortClause}) {
      edges {
        role
        node {
          ${STAFF_FIELDS}
        }
      }
    }`);
  }

  if (include.recommendations) {
    const perPage = clampPerPage(
      typeof include.recommendations === "object" ? (include.recommendations.perPage ?? 10) : 10,
    );
    extra.push(`
    recommendations(perPage: ${perPage}, sort: [RATING_DESC]) {
      nodes {
        ${MEDIA_RECOMMENDATION_FIELDS}
      }
    }`);
  }

  if (include.streamingEpisodes) {
    extra.push(`
    streamingEpisodes {
      title
      thumbnail
      url
      site
    }`);
  }

  if (include.externalLinks) {
    extra.push(`
    externalLinks {
      id
      url
      site
      type
      icon
      color
    }`);
  }

  if (include.stats) {
    extra.push(`
    stats {
      scoreDistribution { score amount }
      statusDistribution { status amount }
    }`);
  }

  if (include.customFields) {
    extra.push(include.customFields);
  }

  return extra;
}

/**
 * Build a `Media(id: $id)` query that optionally includes characters, staff,
 * relations, streaming episodes, external links, stats, and recommendations.
 *
 * When no include options are given, the query is identical to QUERY_MEDIA_BY_ID.
 *
 * @internal
 */
export function buildMediaByIdQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_MEDIA_BY_ID;

  return `
query ($id: Int!) {
  Media(id: $id) {
    ${MEDIA_FIELDS_BASE}
    ${buildMediaIncludeQuery(include).join("\n")}
  }
}`;
}

/**
 * Build a search media query that optionally includes characters, staff,
 * relations, streaming episodes, external links, stats, and recommendations.
 *
 * When no include options are given, the query is identical to
 * QUERY_MEDIA_SEARCH.
 *
 * @internal
 */
export function buildSearchMediaQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_MEDIA_SEARCH;

  return `
  query (
    $search: String,
    $countryOfOrigin: CountryCode,
    $type: MediaType,
    $format: MediaFormat,
    $format_in: [MediaFormat],
    $status: MediaStatus,
    $season: MediaSeason,
    $seasonYear: Int,
    $genre: String,
    $tag: String,
    $genre_in: [String],
    $tag_in: [String],
    $genre_not_in: [String],
    $tag_not_in: [String],
    $isAdult: Boolean,
    $idNotIn: [Int],
    $sort: [MediaSort],
    $page: Int,
    $perPage: Int
  ) {
    Page(page: $page, perPage: $perPage) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      media(
        search: $search,
        countryOfOrigin: $countryOfOrigin,
        type: $type,
        format: $format,
        format_in: $format_in,
        status: $status,
        season: $season,
        seasonYear: $seasonYear,
        genre: $genre,
        tag: $tag,
        genre_in: $genre_in,
        tag_in: $tag_in,
        genre_not_in: $genre_not_in,
        tag_not_in: $tag_not_in,
        isAdult: $isAdult,
        id_not_in: $idNotIn,
        sort: $sort
      ) {
        ${MEDIA_FIELDS_BASE}
        ${buildMediaIncludeQuery(include).join("\n")}
      }
    }
  }`;
}

/**
 * Build a Media query, sorted trending descendingly, that optionally includes characters, staff,
 * relations, streaming episodes, external links, stats, and recommendations.
 *
 * When no include options are given, the query is identical to QUERY_TRENDING.
 *
 * @internal
 */
export function buildGetTrendingQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_TRENDING;

  return `
  query (
    $type: MediaType,
    $isAdult: Boolean,
    $idNotIn: [Int],
    $page: Int,
    $perPage: Int
  ) {
    Page(page: $page, perPage: $perPage) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      media(
        type: $type,
        isAdult: $isAdult,
        id_not_in: $idNotIn,
        sort: TRENDING_DESC
      ) {
        ${MEDIA_FIELDS_BASE}
        ${buildMediaIncludeQuery(include).join("\n")}
      }
    }
  }`;
}

/**
 * Build a Media query to fetch by season that optionally includes characters,
 * staff, relations, streaming episodes, external links, stats, and
 * recommendations.
 *
 * When no include options are given, the query is identical to QUERY_MEDIA_BY_SEASON.
 *
 * @internal
 */
export function buildGetMediaBySeasonQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_MEDIA_BY_SEASON;

  return `
  query (
    $season: MediaSeason!,
    $seasonYear: Int!,
    $type: MediaType,
    $isAdult: Boolean,
    $idNotIn: [Int],
    $sort: [MediaSort],
    $page: Int,
    $perPage: Int
  ) {
    Page(page: $page, perPage: $perPage) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      media(
        season: $season,
        seasonYear: $seasonYear,
        type: $type,
        isAdult: $isAdult,
        id_not_in: $idNotIn,
        sort: $sort
      ) {
        ${MEDIA_FIELDS_BASE}
        ${buildMediaIncludeQuery(include).join("\n")}
      }
    }
  }`;
}

/**
 * Build a query to fetch recently updated manga that optionally includes
 * characters, staff, relations, streaming episodes, external links, stats, and
 * recommendations.
 *
 * When no include options are given, the query is identical to
 * QUERY_RECENT_CHAPTERS.
 *
 * @internal
 */
export function buildGetRecentlyUpdatedMangaQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_RECENT_CHAPTERS;

  return `
  query (
    $isAdult: Boolean,
    $idNotIn: [Int],
    $page: Int,
    $perPage: Int
  ) {
    Page(page: $page, perPage: $perPage) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      media(
        type: MANGA,
        isAdult: $isAdult,
        id_not_in: $idNotIn,
        status: RELEASING,
        sort: UPDATED_AT_DESC
      ) {
        ${MEDIA_FIELDS_BASE}
        ${buildMediaIncludeQuery(include).join("\n")}
      }
    }
  }`;
}

/**
 * Build a query to fetch upcoming media that optionally includes
 * characters, staff, relations, streaming episodes, external links, stats, and
 * recommendations.
 *
 * When no include options are given, the query is identical to
 * QUERY_PLANNING.
 *
 * @internal
 */
export function buildGetPlanningQuery(include?: MediaIncludeOptions): string {
  if (!include) return QUERY_PLANNING;

  return `
  query (
    $type: MediaType,
    $isAdult: Boolean,
    $idNotIn: [Int],
    $sort: [MediaSort],
    $page: Int,
    $perPage: Int
  ) {
    Page(page: $page, perPage: $perPage) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      media(
        type: $type,
        isAdult: $isAdult,
        id_not_in: $idNotIn,
        status: NOT_YET_RELEASED,
        sort: $sort
      ) {
        ${MEDIA_FIELDS_BASE}
        ${buildMediaIncludeQuery(include).join("\n")}
      }
    }
  }`;
}

export function buildMediaCharactersQuery(
  options: { perPage?: number; sort?: boolean; voiceActors?: boolean } = {},
): string {
  const sortClause = options.sort === false ? "" : ", sort: [ROLE, RELEVANCE, ID]";
  const voiceActorBlock = options.voiceActors
    ? `\n            voiceActors {
              ${VOICE_ACTOR_FIELDS_COMPACT}
            }`
    : "";

  return `
query ($mediaId: Int!, $page: Int, $perPage: Int) {
  Media(id: $mediaId) {
    characters(page: $page, perPage: $perPage${sortClause}) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      edges {
        role
        node {
          ${CHARACTER_FIELDS_COMPACT}
        }${voiceActorBlock}
      }
    }
  }
}`;
}

export function buildMediaStaffQuery(options: { perPage?: number; sort?: boolean } = {}): string {
  const sortClause = options.sort === false ? "" : ", sort: [RELEVANCE, ID]";

  return `
query ($mediaId: Int!, $page: Int, $perPage: Int) {
  Media(id: $mediaId) {
    staff(page: $page, perPage: $perPage${sortClause}) {
      pageInfo { total perPage currentPage lastPage hasNextPage }
      edges {
        role
        node {
          ${STAFF_FIELDS}
        }
      }
    }
  }
}`;
}

/** @internal Build a batched GraphQL query using aliases. */
function buildBatchQuery(ids: number[], typeName: string, fields: string, prefix: string): string {
  const aliases = ids.map((id, i) => `${prefix}${i}: ${typeName}(id: ${id}) { ${fields} }`).join("\n  ");
  return `query {\n  ${aliases}\n}`;
}

export const buildBatchMediaQuery = (ids: number[]): string => buildBatchQuery(ids, "Media", MEDIA_FIELDS, "m");

export const buildBatchCharacterQuery = (ids: number[]): string =>
  buildBatchQuery(ids, "Character", CHARACTER_FIELDS, "c");

export const buildBatchStaffQuery = (ids: number[]): string => buildBatchQuery(ids, "Staff", STAFF_FIELDS, "s");
